import React, { FC, useCallback, useEffect, useState } from "react";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import {
  createBillingAddressValidationShape,
  createCreditCardValidationShape,
  getBillingAddressPayload,
  getRestorableCreditCardFormValues,
  mergeCreditCardFormValues,
} from "components/CreditCardForm/creditCardFormHelper";
import CustomsDeclarationV2 from "components/CustomDeclaration/CustomsDeclarationV2";
import {
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { CustomInformationDto } from "types/api/cart";
import { OpenRightPanel } from "types/common/rightPanel";
import { CreditCardFormValues } from "types/creditCard";
import * as yup from "yup";
import { usePayPalAwareUserPaymentMethods } from "../../../../branding/usePayPalAvailability";
import { useUsdtDepositsEnabled } from "../../../../branding/useUsdtDeposits";
import CreditCardForm from "../../../../components/CreditCardForm/CreditCardForm";
import {
  PaymentButtonSlot,
  PaymentFooter,
} from "../../../../components/PaymentButton";
import PayPalOrderButton from "../../../../components/PayPalOrderButton/PayPalOrderButton";
import StripePaymentForm from "../../../../components/StripePaymentForm";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import {
  createCustomsDeclaration,
  getOutgoing,
  movePackageFromWaitingPaymentToPaid,
} from "../../../../reduxFolder/reducers/outgoing";
import {
  checkout,
  checkoutCreditCard,
  checkoutOverpay,
  clearPurchase,
} from "../../../../reduxFolder/reducers/purchase";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import {
  authCreditCardFeePercent,
  authOverpayFeePercent,
  authPaypalFeePercent,
  authSelectUser,
  authStripeFeePercent,
} from "../../../../reduxFolder/selectors/globalSelectors";
import {
  outgoingSelectDetailedItems,
  outgoingSelectPaymentData,
} from "../../../../reduxFolder/selectors/outgoingSelectors";
import { purchaseSelectId } from "../../../../reduxFolder/selectors/purchaseSelectors";
import {
  PaymentFormButtonSlot,
  PaymentFormFooter,
} from "../../../../styles/paymentFormStyles";
import api from "../../../../utils/api";
import formatter from "../../../../utils/formatter";
import { toFiniteNumber } from "../../../../utils/number";
import {
  calculatePaymentFee,
  getPaymentFeePercent,
} from "../../../../utils/paymentFees";
import {
  getPaymentMethodLabel,
  isPaymentOption,
  toPaymentMethod,
  USDT_PAYMENT_OPTION,
  type PaymentMethod,
  type PaymentOption,
} from "../../../../utils/paymentMethods";
import { extractPayPalOrderId } from "../../../../utils/paypal";
import { finalizeStripePaymentForContext } from "../../../../utils/stripePaymentAdapter";
import Tab from "../../../RightPanel/components/Tab";
import TabsView from "../../../RightPanel/components/TabsView";
import { refreshOutgoingPaymentData } from "../../paymentRefresh";
import PaymentMethodSelector from "../PaymentMethodSelector";
import PriceList from "../PriceList/PriceList";
import UsdtDepositCheckout from "../UsdtDepositCheckout/UsdtDepositCheckout";
import {
  ScrollableContent,
  Section,
  SectionTitle,
} from "./ReviewAndPayPanel.styles";

interface ReviewAndPayPanelProps {
  panelData: any;
  isOpen: boolean;
  detailedItem: any;
  paymentData: any;
  purchaseId: string | number;
  openRightPanel: OpenRightPanel;
  createCustomsDeclaration: (data: CustomInformationDto) => Promise<void>;
  checkoutCreditCard: (data: any) => Promise<void>;
  checkoutOverpay: (data: any) => Promise<void>;
  checkout: (data: any) => Promise<void>;
  closeRightPanel: () => void;
  clearPurchase: () => void;
  getOutgoing: (data?: any) => Promise<void>;
  getProfile: () => Promise<unknown>;
  movePackageFromWaitingPaymentToPaid: (id: string | number) => void;
  user: any;
  authCreditCardFeePercent: string;
  authOverpayFeePercent: string;
  authPaypalFeePercent: string;
  authStripeFeePercent: string;
}

const ReviewAndPayPanel: FC<ReviewAndPayPanelProps> = React.memo(
  ({
    checkout,
    checkoutOverpay,
    checkoutCreditCard,
    panelData,
    isOpen,
    purchaseId,
    detailedItem,
    paymentData,
    openRightPanel,
    closeRightPanel,
    clearPurchase,
    getOutgoing,
    getProfile,
    movePackageFromWaitingPaymentToPaid,
    createCustomsDeclaration,
    user,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
  }) => {
    const { t, i18n } = useTranslation("common");

    const paymentMethodDefaults = {
      creditCardAllowedForUser: false,
      overpayAllowedForUser: false,
    };
    const {
      available: paymentMethods,
      isAvailable,
      paypalConfig,
      isPayPalAvailable,
    } = usePayPalAwareUserPaymentMethods(user, paymentMethodDefaults);
    const usdtEnabled = useUsdtDepositsEnabled();
    const paymentOptions: PaymentOption[] = usdtEnabled
      ? [...paymentMethods, USDT_PAYMENT_OPTION]
      : paymentMethods;
    const restoredPaymentMethod = panelData.get("selectedPaymentMethod");
    const initialSelectedPaymentMethod = isPaymentOption(restoredPaymentMethod)
      ? restoredPaymentMethod
      : undefined;
    // paymentOptions (not paymentMethods) is the single source of truth for
    // the selection, otherwise the reset below would bounce a USDT choice.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
      PaymentOption | undefined
    >(
      initialSelectedPaymentMethod &&
        paymentOptions.includes(initialSelectedPaymentMethod)
        ? initialSelectedPaymentMethod
        : paymentOptions[0],
    );
    useEffect(() => {
      if (
        selectedPaymentMethod &&
        paymentOptions.includes(selectedPaymentMethod)
      ) {
        return;
      }

      const nextPaymentMethod = paymentOptions[0];
      if (selectedPaymentMethod !== nextPaymentMethod) {
        setSelectedPaymentMethod(nextPaymentMethod);
      }
    }, [paymentOptions, selectedPaymentMethod]);

    const id = panelData.get("detailedItemID");
    const editCustomsData = panelData.get("isEditCustomData", false);
    const historyConsolidation = panelData.get("consolidation");
    const detailed =
      historyConsolidation !== undefined
        ? historyConsolidation
        : detailedItem.get(id, Map());
    const estimate = detailed.get("estimate", Map());
    const declarations = detailed.get("customs_declarations", List());
    const total = estimate.get("total");
    const userTotal = estimate.get("user_total");
    const initialCreditCardValues = mergeCreditCardFormValues(
      i18n.language,
      user.get("country", "US"),
      panelData.get("creditCardFormValues"),
    );

    const paymentFeePercent = getPaymentFeePercent(
      toPaymentMethod(selectedPaymentMethod),
      {
        creditCard: authCreditCardFeePercent,
        overpay: authOverpayFeePercent,
        paypal: authPaypalFeePercent,
        stripe: authStripeFeePercent,
      },
    );
    const commissionFeeValue = calculatePaymentFee(
      userTotal,
      paymentFeePercent,
    );

    // Invoiced users are billed afterwards: BE accepts balance payments that
    // drive the balance negative, so they never need another payment method.
    const isInvoiced = user.get("invoiced", false);
    const shouldPayOnlyFromBalance = userTotal <= 0 || isInvoiced;
    const toPayTotal = shouldPayOnlyFromBalance
      ? total
      : formatter.roundTo2Decimal(userTotal + commissionFeeValue);

    const state = detailed.get("state");
    const isPaid = state === "paid" || state === "shipped";

    const continuesToSelectedMethod = (method: PaymentMethod) =>
      t("common.continueToPaymentMethod", {
        paymentMethod: getPaymentMethodLabel(t, method),
      });

    const creditCardFormik = useFormik<CreditCardFormValues>({
      validateOnBlur: false,
      initialValues: initialCreditCardValues,
      validationSchema: yup.object({
        ...createCreditCardValidationShape(t),
        billingAddress: yup.object(createBillingAddressValidationShape(t)),
      }),
      onSubmit: (values) => {
        const { name, number, expiration, cvv, billingAddress } = values;
        const [month, year] = expiration.split("/");
        const cardNumber = number.split(" ").join("");

        const request = {
          credit_card: {
            card_name: name,
            card_number: cardNumber,
            exp_month: month,
            exp_year: year,
            card_code: cvv,
            ...getBillingAddressPayload(billingAddress),
          },
        };

        checkoutCreditCard({ id, data: request });
      },
    });

    const handleBack = useCallback(() => {
      openRightPanel(panelData.get("returnPreviousPanel"), panelData);
    }, [openRightPanel, panelData]);

    const handlePayFromBalance = useCallback(() => {
      checkout({ id });
    }, [checkout, id]);

    const handlePayViaOverpay = useCallback(() => {
      checkoutOverpay({ id });
    }, [checkoutOverpay, id]);

    const createPayPalOrder = useCallback(async () => {
      const response = await api.purchase.createPayPalOrder(id);
      return extractPayPalOrderId(response);
    }, [id]);

    const refreshCompletedPayment = useCallback(async () => {
      movePackageFromWaitingPaymentToPaid(id);
      await refreshOutgoingPaymentData({
        getOutgoing,
        paymentItemsCount: paymentData.get("items", List()).size,
      });
      closeRightPanel();
      clearPurchase();
    }, [
      clearPurchase,
      closeRightPanel,
      getOutgoing,
      id,
      movePackageFromWaitingPaymentToPaid,
      paymentData,
    ]);

    const completeExternalPayment = useCallback(async () => {
      await api.purchase.confirmation(id);
      await refreshCompletedPayment();
    }, [id, refreshCompletedPayment]);

    const confirmPurchase = useCallback(
      () => api.purchase.confirmation(id),
      [id],
    );

    const capturePayPalOrder = useCallback(
      async (orderId: string) => {
        await api.purchase.capturePayPalOrder(id, { order_id: orderId });
        await completeExternalPayment();
      },
      [completeExternalPayment, id],
    );

    const createStripePaymentIntent = useCallback(
      () => api.purchase.createStripePaymentIntent(id),
      [id],
    );

    const completeStripePaymentIntent = useCallback(
      async (paymentIntentId: string) => {
        await finalizeStripePaymentForContext(
          { flow: "purchase", consolidationId: id },
          paymentIntentId,
        );
        await refreshCompletedPayment();
      },
      [id, refreshCompletedPayment],
    );

    const switchPaymentMethod = useCallback((method: PaymentOption) => {
      setSelectedPaymentMethod(method);
    }, []);

    const handleSubmitButton = () => {
      creditCardFormik.handleSubmit();
    };

    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "REVIEW_AND_PAY",
      getReturnPanelData: () =>
        panelData
          .set("selectedPaymentMethod", selectedPaymentMethod)
          .set(
            "creditCardFormValues",
            getRestorableCreditCardFormValues(creditCardFormik.values),
          ),
    };

    const paymentForm = (method: PaymentMethod) => {
      switch (method) {
        case "credit_card":
          return (
            <CreditCardForm
              formik={creditCardFormik}
              cardSectionHeader={null}
              fieldsTopSpacing="section"
            />
          );
        default:
          return null;
      }
    };

    const payButton = () => {
      if (shouldPayOnlyFromBalance) {
        return (
          <PaymentButtonSlot>
            <PaymentFooter
              onClick={handlePayFromBalance}
              isLoading={purchaseId === id}
              helpPanelContext={helpPanelContext}
            >
              {`${t("common.continueToBalance")} (${total})`}
            </PaymentFooter>
          </PaymentButtonSlot>
        );
      } else {
        if (!selectedPaymentMethod) {
          return (
            <PaymentButtonSlot>
              <PaymentFooter disabled helpPanelContext={helpPanelContext}>
                {t("common.noPaymentMethodsAvailable")}
              </PaymentFooter>
            </PaymentButtonSlot>
          );
        }

        if (selectedPaymentMethod === USDT_PAYMENT_OPTION) {
          return (
            <UsdtDepositCheckout
              targetAmount={toFiniteNumber(userTotal)}
              sessionKey={`purchase:${id}`}
              isOpen={isOpen}
              helpPanelContext={helpPanelContext}
              confirmPurchase={confirmPurchase}
              refreshProfile={getProfile}
              onPaid={refreshCompletedPayment}
            />
          );
        }

        const title = `${continuesToSelectedMethod(
          selectedPaymentMethod,
        )} (${toPayTotal})`;

        if (selectedPaymentMethod === "paypal") {
          return (
            <PayPalOrderButton
              paypalConfig={paypalConfig}
              createOrder={createPayPalOrder}
              captureOrder={capturePayPalOrder}
              fallbackChildren={title}
              helpPanelContext={helpPanelContext}
              disabled={!isPayPalAvailable}
            />
          );
        }

        if (selectedPaymentMethod === "stripe") {
          return (
            <StripePaymentForm
              createPaymentIntent={createStripePaymentIntent}
              completePaymentIntent={completeStripePaymentIntent}
              returnUrl={`${window.location.origin}/parcels/outgoing/${id}/stripe-return`}
              fallbackChildren={title}
              helpPanelContext={helpPanelContext}
            />
          );
        }

        if (selectedPaymentMethod === "overpay") {
          return (
            <PaymentButtonSlot>
              <PaymentFooter
                onClick={handlePayViaOverpay}
                isLoading={purchaseId === id}
                disabled={!!purchaseId}
                helpPanelContext={helpPanelContext}
              >
                {title}
              </PaymentFooter>
            </PaymentButtonSlot>
          );
        } else {
          return (
            <PaymentFormFooter>
              <PaymentFormButtonSlot>
                <PaymentFooter
                  onClick={handleSubmitButton}
                  isLoading={purchaseId === id}
                  disabled={!!purchaseId || !creditCardFormik.isValid}
                  helpPanelContext={helpPanelContext}
                >
                  {title}
                </PaymentFooter>
              </PaymentFormButtonSlot>
            </PaymentFormFooter>
          );
        }
      }
    };

    const paymentMethodsSection = (
      <>
        <Section>
          <PaymentMethodSelector
            paymentMethods={paymentOptions}
            selectedPaymentMethod={selectedPaymentMethod}
            onSelect={switchPaymentMethod}
          />
          {selectedPaymentMethod === "credit_card" &&
            isAvailable("credit_card") &&
            paymentForm("credit_card")}
        </Section>
        {payButton()}
      </>
    );

    const paymentOnlyFromBalance = (
      <>
        <Section>
          <SectionTitle>{t("parcels.paymentByBalance")}</SectionTitle>
        </Section>
        {payButton()}
      </>
    );

    return (
      <ContentWrapper>
        <Heading>
          <PanelBackButton onClick={handleBack}>
            {detailed.get("sku")}:
          </PanelBackButton>
          {isPaid ? t("parcels.paymentPaid") : t("common.reviewAndPay")}
        </Heading>
        <TabsView>
          <Tab title={t("parcels.details")}>
            <ScrollableContent>
              <PriceList
                parcel={detailed}
                selectedPaymentMethod={toPaymentMethod(selectedPaymentMethod)}
                payFromBalance={shouldPayOnlyFromBalance}
              />
              {!isPaid &&
                (shouldPayOnlyFromBalance
                  ? paymentOnlyFromBalance
                  : paymentMethodsSection)}
            </ScrollableContent>
          </Tab>
          <Tab title={t("parcels.customsData")}>
            <Content>
              <CustomsDeclarationV2
                itemId={id}
                readOnly={!editCustomsData}
                declarations={declarations}
                create={createCustomsDeclaration}
                noPadding
              />
            </Content>
          </Tab>
        </TabsView>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    purchaseId: purchaseSelectId,
    detailedItem: outgoingSelectDetailedItems,
    paymentData: outgoingSelectPaymentData,
    user: authSelectUser,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
  }),
  {
    checkout,
    checkoutOverpay,
    checkoutCreditCard,
    clearPurchase,
    closeRightPanel,
    getOutgoing,
    getProfile,
    movePackageFromWaitingPaymentToPaid,
    openRightPanel,
    createCustomsDeclaration,
  },
);

export default withConnect(ReviewAndPayPanel);
